<template lang="pug">
.account-page.flex.align-start.justify-center
  .card-style.content.flex.column.align-stretch.gap-10.flex-1
    .title.font-size-20.bold 個人資料
    .desc *更改資料需聯絡個管師
    .name.text-secondary 姓名
    .value {{ profile?.family_name }}{{ profile?.given_name }}
    .name.text-secondary 信箱
    .value {{ profile?.email }}
    .name.text-secondary 職稱
    .value {{ profile?.title }}
    .name.text-secondary 照片
    .value
      img.pro-img(v-if="profile?.avatar", :src="profile.avatar")
      .text-secondary(v-else) 未設定照片
    el-button(type="primary", @click="showPasswordSetting") 變更密碼
    el-button(type="primary", @click="showAuthenticatorSetting") 變更雙重驗證
  .card-style.content.flex.column.align-stretch.gap-10.flex-1
    .flex.align-center.justify-space-between
      .title.font-size-20.bold 可排案時段
      el-button(type="primary", size="small", :disabled="!availability", @click="showAvailabilityEditor") 編輯
    template(v-if="availability")
      .desc 僅供參考，不會因此限制建立預約
      .name.text-secondary 每週可排案時段
      .value(v-if="!configured") 未設定（全時段可排案）
      .value(v-else)
        .week-row.flex.gap-10(v-for="row in weeklySummary", :key="row.weekday")
          .week-name {{ row.name }}
          .text-secondary(v-if="row.text === ''") 無
          div(v-else) {{ row.text }}
      .name.text-secondary 國定假日照常
      .value {{ availability.holidays_available ? '是' : '否' }}
      .name.text-secondary 未來的不可排案日期
      .value
        .text-secondary(v-if="futureUnavailable.length === 0") 無
        .off-row(v-for="row in futureUnavailable", :key="row.key")
          span {{ row.dateText }}
          span.time {{ row.timeText }}
          span.text-secondary.note(v-if="row.note") {{ row.note }}
      .text-secondary(v-if="updateText") {{ updateText }}
    .text-secondary(v-else) 載入失敗
  availability-editor(
    ref="availabilityEditor"
    :loader="getMyAvailability"
    :saver="updateMyAvailability"
    :branch-loader="availabilityBranchLoader"
    @finish="finishAvailability")
  password-setting(ref="passwordSetting")
  authenticator-setting(ref="authenticatorSetting")
</template>

<script>
import {
  getProfile,
  getMyAvailability,
  updateMyAvailability,
} from '@/api/auth';
import { getBranches } from '@/api/place';
import ProfessionalAvailabilityEditor from '@/components/drawers/ProfessionalAvailabilityEditor.vue';
import {
  describePartialOff,
  describeUpdatedBy,
  hasWeeklySlots,
  mergeSlots,
  minuteToTime,
  reasonText,
  timeToMinute,
  toDateKey,
} from '@/util/availability';
import msg from '@/util/msg';
import PasswordSetting from '@/site/professional/components/drawer/PasswordSetting.vue';
import AuthenticatorSetting from '@/site/professional/components/drawer/AuthenticatorSetting.vue';

export default {
  components: {
    PasswordSetting,
    AuthenticatorSetting,
    'availability-editor': ProfessionalAvailabilityEditor,
  },
  data() {
    return {
      profile: {},
      availability: null,
    };
  },
  computed: {
    configured() {
      return hasWeeklySlots(this.availability);
    },
    weeklySummary() {
      return [1, 2, 3, 4, 5, 6, 7].map((weekday) => {
        const slots = mergeSlots((this.availability?.weekly || [])
          .filter((w) => w.weekday === weekday)
          .map((w) => ({ start: timeToMinute(w.start), end: timeToMinute(w.end) })));
        return {
          weekday,
          name: msg.availability.weekdayNames[weekday],
          text: slots.map((s) => `${minuteToTime(s.start)}–${minuteToTime(s.end)}`).join('、'),
        };
      });
    },
    // 整天的一筆一行；同一天的指定時段合併成一行
    futureUnavailable() {
      const today = toDateKey(new Date());
      const rows = [];
      const partialByDate = {};
      (this.availability?.unavailable || [])
        .filter((u) => u.end_date >= today)
        .forEach((u) => {
          if (!u.start) {
            rows.push({
              key: `${u.start_date}-${u.end_date}-${rows.length}`,
              sortKey: `${u.start_date} 0`,
              dateText: u.start_date === u.end_date ? u.start_date : `${u.start_date} 至 ${u.end_date}`,
              timeText: '整天',
              note: reasonText(u.reason),
            });
            return;
          }
          if (!partialByDate[u.start_date]) {
            partialByDate[u.start_date] = [];
          }
          partialByDate[u.start_date].push({ start: timeToMinute(u.start), end: timeToMinute(u.end), reason: u.reason });
        });
      Object.entries(partialByDate).forEach(([date, slots]) => {
        rows.push({
          key: date,
          sortKey: `${date} 1`,
          dateText: date,
          timeText: describePartialOff(slots.sort((a, b) => a.start - b.start)),
          note: '',
        });
      });
      return rows.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    },
    updateText() {
      if (!this.availability?.updated_at) {
        return '';
      }
      const at = this.$timestampToDateTimeMinute(this.availability.updated_at);
      return [`最後更新：${at}`, describeUpdatedBy(this.availability.updated_by)].filter(Boolean).join('，');
    },
  },
  methods: {
    getMyAvailability,
    updateMyAvailability,
    async loadAvailability() {
      try {
        this.availability = await getMyAvailability();
      } catch (e) {
        this.availability = null;
        this.$showAxiosException('載入可排案時段失敗', e);
      }
    },
    async availabilityBranchLoader() {
      const branches = await getBranches();
      return {
        branches: branches.map((b) => ({ id: b.id, name: b.name })),
        registeredIds: (this.profile?.branches || []).map((b) => b.id),
      };
    },
    showAvailabilityEditor() {
      this.$refs.availabilityEditor.$emit('show');
    },
    finishAvailability(result) {
      if (result) {
        this.$execWithLoading(this.loadAvailability);
      }
    },
    loadData() {
      this.$execWithLoading(async () => {
        const data = await getProfile();
        this.profile = data;
        await this.loadAvailability();
      }, (e) => {
        this.$showAxiosException('載入個人資料失敗', e);
      });
    },
    showPasswordSetting() {
      this.$refs.passwordSetting.$emit('show');
    },
    showAuthenticatorSetting() {
      this.$refs.authenticatorSetting.$emit('show');
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style lang="scss" scoped>
.account-page {
  padding: 20px 30px;
  background: #F3F3F3;
  .content {
    max-width: 600px;
    padding: 20px;
    .el-button {
      margin: 0;
    }
    .off-row .time,
    .off-row .note {
      margin-left: 10px;
    }
    .week-row .week-name {
      flex: 0 0 40px;
    }
    .pro-img {
      width: 80px;
      height: 80px;
      object-fit: cover;
    }
  }
}
</style>
